import React, { useRef, useEffect, useCallback } from 'react';
import { PdfFormField, getTokenDisplayLabel } from '../PdfFormTemplates/types';
import { pdfToScreen } from '../PdfFormTemplates/PdfFormEditor/coordinateUtils';
import { isValidNumericFieldValue, normalizeNumericInput, normalizeNumericValue } from './numericFieldUtils';
import styles from './pdfFormSign.module.css';

interface Props {
  field: PdfFormField;
  fieldValueKey: string;
  value: string | boolean | number;
  scale: number;
  pageHeight: number;
  onChange: (fieldValueKey: string, value: string | boolean | number) => void;
  showRequired?: boolean;
  focused?: boolean;
  onAdvance?: () => void;
}

function formatDateDisplay(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
}

function FieldInputOverlay({ field, fieldValueKey, value, scale, pageHeight, onChange, showRequired, focused, onAdvance }: Props) {
  const pos = pdfToScreen(field.x, field.y, field.width, field.height, pageHeight, scale);
  let hasValue = !!value;
  if (field.field_type === 'number') hasValue = isValidNumericFieldValue(value);
  else if (typeof value === 'string') hasValue = value.length > 0;
  else if (typeof value === 'number') hasValue = true;
  const bg = hasValue ? '#fff' : 'transparent';
  const isRequired = !!field.required;
  const requiredClass = (isRequired && !hasValue) || showRequired ? ` ${styles.fieldRequired}` : '';
  const maxFontSize = field.font_size ? field.font_size * scale : 12;
  const fontSize = Math.min(maxFontSize, pos.height * 0.85);
  const inputRef = useRef<HTMLInputElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);

  const openDatePicker = useCallback(() => {
    const el = dateInputRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      el.showPicker();
    } else {
      // Fallback: make the native input interactive so tap opens picker
      el.style.pointerEvents = 'auto';
      el.focus();
      el.click();
    }
  }, []);

  useEffect(() => {
    if (focused) {
      if (field.field_type === 'date' && dateInputRef.current) {
        dateInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        openDatePicker();
      } else if (inputRef.current) {
        inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        inputRef.current.focus();
      }
    }
  }, [focused, field.field_type, openDatePicker]);

  const requiredStar = isRequired ? (
    <span
      className={styles.requiredStar}
      style={{
        left: pos.x + pos.width + 2,
        top: pos.y - 2,
        fontSize: Math.max(12, pos.height * 0.7),
      }}
    >
      *
    </span>
  ) : null;

  if (field.field_type === 'date') {
    const dateBg = hasValue ? '#fff' : 'rgba(13, 110, 253, 0.06)';
    const dateStr = typeof value === 'string' ? value : '';
    const displayDate = formatDateDisplay(dateStr);
    return (
      <>
        {/* Visible formatted display */}
        <div
          tabIndex={0}
          className={`${styles.fieldTextInput} ${styles.fieldDateInput}${requiredClass}`}
          style={{
            left: pos.x,
            top: pos.y,
            width: pos.width,
            height: pos.height,
            fontSize,
            background: dateBg,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            color: hasValue ? '#212529' : '#adb5bd',
          }}
          onClick={openDatePicker}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') openDatePicker();
          }}
          role="button"
          aria-label={field.field_name}
        >
          {displayDate || 'Select date'}
        </div>
        {/* Native date input — positioned on top, visually transparent, click opens picker */}
        <input
          ref={dateInputRef}
          type="date"
          className={styles.fieldDateInput}
          style={{
            position: 'absolute',
            left: pos.x,
            top: pos.y,
            width: pos.width,
            height: pos.height,
            opacity: 0,
          }}
          value={dateStr}
          onClick={openDatePicker}
          onChange={(e) => {
            // Don't auto-advance on date selection — it jumps the user to the
            // next field (and often the next page) the instant they pick a
            // date. Other field types only advance on Tab/Enter.
            onChange(fieldValueKey, e.target.value);
          }}
          tabIndex={-1}
          aria-hidden
        />
        {requiredStar}
      </>
    );
  }

  if (field.field_type === 'checkbox') {
    return (
      <>
        <div
          className={`${styles.fieldInputWrapper}${requiredClass}`}
          style={{
            left: pos.x,
            top: pos.y,
            width: pos.width,
            height: pos.height,
          }}
        >
          <input
            ref={inputRef}
            type="checkbox"
            className={styles.fieldCheckbox}
            checked={!!value}
            onChange={(e) => {
              onChange(fieldValueKey, e.target.checked);
              if (e.target.checked && onAdvance) onAdvance();
            }}
            aria-label={field.field_name}
          />
        </div>
        {requiredStar}
      </>
    );
  }

  let inputType = 'text';
  if (field.field_type === 'email') inputType = 'email';
  else if (field.field_type === 'phone') inputType = 'tel';

  const formatPhone = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (field.field_type === 'phone') {
      onChange(fieldValueKey, formatPhone(e.target.value));
    } else if (field.field_type === 'number') {
      onChange(fieldValueKey, normalizeNumericInput(e.target.value));
    } else {
      onChange(fieldValueKey, e.target.value);
    }
  };

  if (field.field_type === 'number') {
    const isCurrency = field.numeric_format === 'currency';
    const numberValue = normalizeNumericValue(value);
    return (
      <>
        <div
          className={`${styles.fieldNumericWrapper}${requiredClass}`}
          style={{
            left: pos.x,
            top: pos.y,
            width: pos.width,
            height: pos.height,
            background: bg,
          }}
        >
          {isCurrency && <span className={styles.fieldCurrencySymbol}>$</span>}
          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            className={`${styles.fieldTextInput} ${styles.fieldInlineInput} ${isCurrency ? styles.fieldCurrencyInput : ''}`}
            style={{
              flex: 1,
              minWidth: 0,
              height: '100%',
              fontSize,
              background: 'transparent',
            }}
            value={numberValue}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === 'Tab' || e.key === 'Enter') {
                e.preventDefault();
                if (onAdvance && isValidNumericFieldValue(numberValue)) onAdvance();
              }
            }}
            placeholder={getTokenDisplayLabel(field.field_name)}
            aria-label={field.field_name}
          />
        </div>
        {requiredStar}
      </>
    );
  }

  return (
    <>
      <input
        ref={inputRef}
        type={inputType}
        className={`${styles.fieldTextInput}${requiredClass}`}
        style={{
          left: pos.x,
          top: pos.y,
          width: pos.width,
          height: pos.height,
          fontSize,
          background: bg,
        }}
        value={typeof value === 'string' ? value : ''}
        onChange={handleChange}
        onKeyDown={(e) => {
          if (e.key === 'Tab' || e.key === 'Enter') {
            e.preventDefault();
            if (onAdvance) onAdvance();
          }
        }}
        placeholder={getTokenDisplayLabel(field.field_name)}
        aria-label={field.field_name}
      />
      {requiredStar}
    </>
  );
}

export { FieldInputOverlay };
